/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import {
  useReducer, useCallback, useMemo, useEffect,
} from 'react';

import { List, Map } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import useBlobResponseHandler from './useBlobResponseHandler';
import useUnpersistedResponsesTracker, { FlushStrategy } from './useUnpersistedResponsesTracker';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const fileEnabledQuestionTypes = ['audio', 'audio/text', 'video', 'file_upload', 'picture'];
const singleFileQuestionTypes = ['audio', 'audio/text', 'video', 'picture'];

/* ––
 * ––– Helper functions
 * –––––––––––––––––––––––––––––––– */
function setBlobName(blobs, applicationId) {
  if (isEmpty(blobs)) return blobs;

  blobs.forEach((blob) => {
    const extension = blob.type.split('/').pop();
    // eslint-disable-next-line no-param-reassign
    blob.name = blob.name ? blob.name : `app${applicationId}-${Date.now()}.${extension}`;
  });

  return blobs;
}

function isFileResponse(response) {
  return (
    (response.get('file') && response.get('file') !== '')
    || (
      response.get('blobs')
      && response.get('blobs').length > 0
      && response.get('blobs').every((blob) => blob.get('signedBlobId'))
    )
    || response.get('value') !== ''
  );
}

function buildResponsePayload(response) {
  // Signed blobs id are stored in the blobs property
  // We need to populate the docs field with only the signed blobs id
  let payload = new Map(response);

  if (payload.get('blobs')) {
    const docsPayload = payload.get('blobs').map((blob) => ({ file: blob.get('signedBlobId'), recognition: blob.get('recognition') }));
    payload = payload.set('docs', docsPayload);
  }

  // Remove any unnecessary fields
  return payload.delete('blobs')
    .delete('blob')
    .delete('duration')
    .delete('forced')
    .delete('suggested');
}

/* ––
 * ––– State reducer
 * –––––––––––––––––––––––––––––––– */
const StateAction = Object.freeze({
  AddResponse:            'AddResponse',
  RemoveResponse:         'RemoveResponse',
  UpdateValidResponses:   'UpdateValidResponses',
  UpdateSubmissionStatus: 'UpdateSubmissionStatus',
});

const defaultState = new Map({
  responses:      new List(),
  validResponses: new List(),
  submittable:    false,
});

function stateReducer(state, action) {
  switch (action.type) {
    case StateAction.AddResponse: {
      const responseIndex = state.get('responses').findIndex((response) => response.get('promptId') === action.promptId);

      if (responseIndex === -1) {
        return state.update('responses', (responses) => responses.push(action.response));
      }

      return state.setIn(['responses', responseIndex], action.response);
    }
    case StateAction.RemoveResponse: {
      const responseIndex = state.get('responses').findIndex((response) => response.get('promptId') === action.promptId);

      if (responseIndex >= 0) {
        return state.update('responses', (responses) => responses.delete(responseIndex));
      }

      return state;
    }
    case StateAction.UpdateValidResponses: {
      return state.set('validResponses', action.responses);
    }
    case StateAction.UpdateSubmissionStatus: {
      if (state.get('submittable') !== action.value) {
        return state.set('submittable', action.value);
      }

      return state;
    }
    default: {
      throw new Error(`Invalid action type: ${action.type}`);
    }
  }
}

/* ––
 * ––– Hook
 * –––––––––––––––––––––––––––––––– */
function useFormStateHandler(
  applicationId, questions, onSave, onGenerateResponseSaveMetadata, options = {},
) {
  /* –– State declaration
   * –––––––––––––––––––––––––––––––– */
  const [state, dispatch] = useReducer(stateReducer, defaultState);

  const responses = state.get('responses');
  const validResponses = state.get('validResponses');
  const submittable = state.get('submittable');

  const [
    currentBlobResponse,
    processedBlobResponse,
    processingBlobResponse,
    blobResponseProcessingError,
    processBlobResponse,
  ] = useBlobResponseHandler();

  /* –– Computed values
   * –––––––––––––––––––––––––––––––– */
  /**
   * Split questions in two groups: file-enabled questions and text-based questions.
   * These groups will help us validate the responses and determine if the form is submittable.
   */
  const questionGroups = useMemo(() => questions.reduce(
    (processedQuestions, question) => {
      const questionType = question.getIn(['prompt', 'categoryName']);
      const promptId = question.getIn(['prompt', 'id']);

      return processedQuestions.update(
        fileEnabledQuestionTypes.includes(questionType) ? 'file' : 'text',
        (questionGroup) => questionGroup.push(promptId),
      );
    },
    new Map({ file: new List(), text: new List() }),
  ), [questions]);

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const saveResponseBatches = useCallback((responseBatches, metadata) => {
    const responseBatchesToPersist = responseBatches
      .map(
        (responseBatch) => responses
          .filter((response) => responseBatch.includes(response?.get('promptId')))
          .map(buildResponsePayload),
      )
      .filter((responseBatch) => responseBatch.size > 0);

    onSave(responseBatchesToPersist, metadata);
  }, [responses, onSave]);

  // This is here because it expose handlers.
  const unpersistedResponsesTracker = useUnpersistedResponsesTracker(
    applicationId,
    saveResponseBatches,
    options,
  );

  const trackResponse = useCallback((promptId, response) => {
    dispatch({ type: StateAction.AddResponse, promptId, response });

    let trackingFlushStrategy = FlushStrategy.OnThresholdReached;
    const flushMetadata = onGenerateResponseSaveMetadata(response);

    if (response.get('forced')) {
      trackingFlushStrategy = FlushStrategy.Realtime;
    } else if (response.get('suggested')) {
      trackingFlushStrategy = FlushStrategy.Manual;
    }

    unpersistedResponsesTracker.trackResponse(promptId, trackingFlushStrategy, flushMetadata);
  }, [onGenerateResponseSaveMetadata, unpersistedResponsesTracker]);

  const processResponse = useCallback((promptId, value) => {
    const text        = value.get('text');
    const number      = value.get('number');
    const blob        = setBlobName(value.get('blob'), applicationId);
    const blobs       = value.get('blobs');
    const geolocation = value.get('geolocation');

    const duration    = value.get('duration');
    const incomplete  = value.get('incomplete');
    const forced      = value.get('forced');
    const suggested   = value.get('suggested');

    if ([text, number, blob, blobs, geolocation].every(isEmpty)) {
      if (forced) {
        unpersistedResponsesTracker.trackResponse(
          promptId,
          FlushStrategy.Realtime,
          onGenerateResponseSaveMetadata(value),
        );
      } else {
        dispatch({ type: StateAction.RemoveResponse, promptId });
        unpersistedResponsesTracker.discardResponse(promptId);
      }
    } else {
      const currentQuestion = questions.find((question) => question.getIn(['prompt', 'id']) === promptId);
      const currentQuestionCategory = currentQuestion.getIn(['prompt', 'categoryName']);

      const response = new Map({
        applicationId,
        value: text || number,
        promptId,
        blob,
        blobs,
        geolocation,
        duration,
        incomplete,
        forced,
        suggested,
      });

      const requiresFileUpload = singleFileQuestionTypes.includes(currentQuestionCategory) && blob;

      if (requiresFileUpload) {
        const [accepted, error] = processBlobResponse(response);

        if (!accepted && Sentry) {
          Sentry.captureException(`Blob response processing was not accepted: ${error}`);
        }
      } else {
        trackResponse(promptId, response);
      }
    }
  }, [
    applicationId,
    questions,
    trackResponse,
    onGenerateResponseSaveMetadata,
    unpersistedResponsesTracker,
    processBlobResponse,
  ]);

  const saveFormProgress = useCallback(() => {
    /**
     * Under single question mode, moving to other question could be treated as
     * compliance with the current response.
     * Therehore, if a suggestion is present, it will be processed.
     */
    unpersistedResponsesTracker.flushResponses([
      FlushStrategy.OnThresholdReached,
      FlushStrategy.Manual,
    ]);
  }, [unpersistedResponsesTracker]);

  const retryFailedResponseProcessing = useCallback(() => {
    const [accepted, error] = processBlobResponse(currentBlobResponse);

    if (!accepted && window.Sentry) {
      window.Sentry.captureException(`Blob response processsing was not accepted: ${error}`);
    }
  }, [currentBlobResponse, processBlobResponse]);

  const submitForm = useCallback((flushMetadata) => {
    unpersistedResponsesTracker.flushResponses([], flushMetadata);
  }, [unpersistedResponsesTracker]);

  /* –– Effects
   * –––––––––––––––––––––––––––––––– */
  useEffect(() => {
    // Filter responses keeping only the valid ones
    const newValidResponses = responses.filter((response) => {
      const promptId = response.get('promptId');

      return (
        (isFileResponse(response) && questionGroups.get('file').includes(promptId))
        || (response.get('value') !== '' && questionGroups.get('text').includes(promptId))
      );
    });

    dispatch({ type: StateAction.UpdateValidResponses, responses: newValidResponses });
    dispatch({
      type:  StateAction.UpdateSubmissionStatus,
      value: newValidResponses.size === questions.size,
    });
  }, [responses, questionGroups]);

  useEffect(() => {
    if (!isEmpty(processedBlobResponse)) {
      trackResponse(processedBlobResponse.get('promptId'), processedBlobResponse);
    }
  }, [processedBlobResponse]);

  /* –– Hook exports
   * –––––––––––––––––––––––––––––––– */
  return {
    responses,
    validResponses,

    submittable,
    processing:              processingBlobResponse,
    responseProcessingError: blobResponseProcessingError,

    processResponse,
    saveFormProgress,
    retryFailedResponseProcessing,
    submitForm,
  };
}

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default useFormStateHandler;
